import { apiRequest, API_BASE_URL, getAuthHeaders } from './api.config';
import { ApiResponse, AuthResponse, AdminUser, AdminRole, MemberUser, Order } from '@utilities/types';

export async function sendOtp(email: string): Promise<ApiResponse<null>> {
    return apiRequest<ApiResponse<null>>('/admin/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
}

export async function verifyOtp(email: string, otp: string): Promise<ApiResponse<AuthResponse>> {
    return apiRequest<ApiResponse<AuthResponse>>('/admin/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ email, otp }),
    });
}

export async function getProfile(): Promise<ApiResponse<AdminUser>> {
    return apiRequest<ApiResponse<AdminUser>>('/admin/profile');
}

export async function updateProfile(data: { firstName?: string; lastName?: string }): Promise<ApiResponse<AdminUser>> {
    return apiRequest<ApiResponse<AdminUser>>('/admin/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
    });
}

export async function getAdmins(): Promise<ApiResponse<AdminUser[]>> {
    return apiRequest<ApiResponse<AdminUser[]>>('/admin/users');
}

export async function inviteAdmin(email: string, role: AdminRole): Promise<ApiResponse<null>> {
    return apiRequest<ApiResponse<null>>('/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify({ email, role }),
    });
}

export async function getMembers(): Promise<ApiResponse<MemberUser[]>> {
    return apiRequest<ApiResponse<MemberUser[]>>('/admin/members');
}

export async function inviteMember(email: string, clientCode: string): Promise<ApiResponse<null>> {
    return apiRequest<ApiResponse<null>>('/admin/members/invite', {
        method: 'POST',
        body: JSON.stringify({ email, clientCode }),
    });
}

export interface MemberClient {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    createdAt: string;
}

export async function getMemberClients(memberId: string): Promise<ApiResponse<MemberClient[]>> {
    return apiRequest<ApiResponse<MemberClient[]>>(`/admin/members/${memberId}/clients`);
}

export async function getOrders(): Promise<ApiResponse<Order[]>> {
    return apiRequest<ApiResponse<Order[]>>('/orders');
}

export async function updateMemberPaymentOptions(
    memberId: string,
    allowDeferredPayment: boolean,
): Promise<ApiResponse<{ id: string; allowDeferredPayment: boolean }>> {
    return apiRequest<ApiResponse<{ id: string; allowDeferredPayment: boolean }>>(
        `/admin/members/${memberId}/payment-options`,
        {
            method: 'PATCH',
            body: JSON.stringify({ allowDeferredPayment }),
        },
    );
}

export async function resetMemberPin(
    memberId: string,
): Promise<ApiResponse<{ id: string; enabled: boolean; hasPin: boolean; protectedAreas: string[] }>> {
    return apiRequest(`/admin/members/${memberId}/pin/reset`, {
        method: 'PATCH',
    });
}

export async function createOrderPaymentLink(
    orderId: string,
): Promise<ApiResponse<Order & { url: string | null }>> {
    return apiRequest<ApiResponse<Order & { url: string | null }>>(
        `/orders/${orderId}/payment-link`,
        { method: 'POST' },
    );
}

export async function downloadOrderPdf(orderId: string): Promise<Blob> {
    const url = `${API_BASE_URL}/orders/${orderId}/admin-pdf`;
    const response = await fetch(url, {
        headers: {
            ...getAuthHeaders(),
        },
    });
    if (!response.ok) {
        throw new Error('Erreur lors du téléchargement du PDF');
    }
    return response.blob();
}

export interface AdminGlobalSettings {
    embroideryMaxCharacters: number;
    visibleSections: Record<string, boolean>;
    visibleItems: Record<string, Record<string, boolean>>;
    visibleButtonCategories: Record<string, boolean>;
    visibleLiasses: Record<string, boolean>;
    visibleFabrics: Record<string, Record<string, boolean>>;
    dreamBoxVisibleLiasses: Record<string, boolean>;
    dreamBoxVisibleFabrics: Record<string, Record<string, boolean>>;
    sectionDefaults: Record<string, string>;
    pricing: AdminPricing;
    orderNotificationEmails: string[];
    legalCgv: string;
    legalCgu: string;
    legalCgvDocuments?: LegalDocument[];
    legalCguDocuments?: LegalDocument[];
}

export interface AdminPricing {
    showPrices: boolean;
    currency: string;
    dreamBoxPrice: number;
    sections: Record<string, number>;
    items: Record<string, Record<string, number>>;
    buttonCategories: Record<string, number>;
    fabricGroups: Record<string, number>;
    fabrics: Record<string, number>;
}

export async function getAdminSettings(): Promise<ApiResponse<AdminGlobalSettings>> {
    return apiRequest<ApiResponse<AdminGlobalSettings>>('/admin/settings');
}

export async function updateAdminEmbroiderySettings(
    embroideryMaxCharacters: number,
): Promise<ApiResponse<{ embroideryMaxCharacters: number }>> {
    return apiRequest<ApiResponse<{ embroideryMaxCharacters: number }>>('/admin/settings/embroidery', {
        method: 'PUT',
        body: JSON.stringify({ embroideryMaxCharacters }),
    });
}

export async function updateAdminVisibleSections(
    visibleSections: Record<string, boolean>,
): Promise<ApiResponse<{ visibleSections: Record<string, boolean> }>> {
    return apiRequest<ApiResponse<{ visibleSections: Record<string, boolean> }>>(
        '/admin/settings/visible-sections',
        {
            method: 'PUT',
            body: JSON.stringify({ visibleSections }),
        },
    );
}

export async function updateAdminVisibleItems(
    visibleItems: Record<string, Record<string, boolean>>,
    visibleButtonCategories: Record<string, boolean>,
): Promise<ApiResponse<{
    visibleItems: Record<string, Record<string, boolean>>;
    visibleButtonCategories: Record<string, boolean>;
}>> {
    return apiRequest<ApiResponse<{
        visibleItems: Record<string, Record<string, boolean>>;
        visibleButtonCategories: Record<string, boolean>;
    }>>(
        '/admin/settings/visible-items',
        {
            method: 'PUT',
            body: JSON.stringify({ visibleItems, visibleButtonCategories }),
        },
    );
}

export async function updateAdminVisibleFabrics(
    payload: { visibleLiasses: Record<string, boolean>; visibleFabrics: Record<string, Record<string, boolean>>; dreamBoxVisibleLiasses: Record<string, boolean>; dreamBoxVisibleFabrics: Record<string, Record<string, boolean>> },
): Promise<ApiResponse<typeof payload>> {
    return apiRequest(
        '/admin/settings/visible-fabrics',
        {
            method: 'PUT',
            body: JSON.stringify(payload),
        },
    );
}

export async function updateAdminSectionDefaults(
    sectionDefaults: Record<string, string>,
): Promise<ApiResponse<{ sectionDefaults: Record<string, string> }>> {
    return apiRequest<ApiResponse<{ sectionDefaults: Record<string, string> }>>(
        '/admin/settings/section-defaults',
        {
            method: 'PUT',
            body: JSON.stringify({ sectionDefaults }),
        },
    );
}

export async function updateAdminPricing(
    pricing: AdminPricing,
): Promise<ApiResponse<{ pricing: AdminPricing }>> {
    return apiRequest<ApiResponse<{ pricing: AdminPricing }>>(
        '/admin/settings/pricing',
        {
            method: 'PUT',
            body: JSON.stringify({ pricing }),
        },
    );
}

export async function updateOrderNotificationEmails(
    orderNotificationEmails: string[],
): Promise<ApiResponse<{ orderNotificationEmails: string[] }>> {
    return apiRequest<ApiResponse<{ orderNotificationEmails: string[] }>>(
        '/admin/settings/order-notification-emails',
        {
            method: 'PUT',
            body: JSON.stringify({ orderNotificationEmails }),
        },
    );
}

export async function updateAdminLegal(
    cgv: string,
    cgu: string,
): Promise<ApiResponse<{ cgv: string; cgu: string }>> {
    return apiRequest<ApiResponse<{ cgv: string; cgu: string }>>(
        '/admin/settings/legal',
        {
            method: 'PUT',
            body: JSON.stringify({ cgv, cgu }),
        },
    );
}

export interface LegalDocument {
    id: string;
    name: string;
    path: string;
    uploadedAt: string;
}

/** URL absolue d'un document légal servi en statique par le backend. */
export function legalDocumentUrl(docPath: string): string {
    const base = (API_BASE_URL || '').replace(/\/api\/v1$/, '');
    return `${base}${docPath}`;
}

export async function uploadLegalDocument(
    type: 'cgv' | 'cgu',
    file: File,
    name?: string,
): Promise<ApiResponse<{ type: string; document: LegalDocument; documents: LegalDocument[] }>> {
    const form = new FormData();
    form.append('type', type);
    if (name) form.append('name', name);
    form.append('document', file);
    // FormData : ne pas fixer Content-Type (le navigateur ajoute la boundary).
    const res = await fetch(`${API_BASE_URL}/admin/settings/legal/documents`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
        body: form,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP error! status: ${res.status}`);
    }
    return res.json();
}

export async function deleteLegalDocument(
    type: 'cgv' | 'cgu',
    docId: string,
): Promise<ApiResponse<{ type: string; documents: LegalDocument[] }>> {
    return apiRequest<ApiResponse<{ type: string; documents: LegalDocument[] }>>(
        `/admin/settings/legal/documents/${type}/${encodeURIComponent(docId)}`,
        { method: 'DELETE' },
    );
}

// ----- Liasses (tissus) — endpoint public -----

export interface AdminTissuItem {
    id: string;
    path: string;
}

export interface AdminLiasse {
    namespace: string;
    namespaceLabel: string;
    items: AdminTissuItem[];
}

export async function getAdminLiasse(namespace: string): Promise<AdminLiasse> {
    const res = await apiRequest<ApiResponse<AdminLiasse>>(`/liasses/${namespace}`);
    return res.data;
}

// Récupère toutes les liasses (source unique : ajout automatique dès qu'une liasse est ajoutée au backend).
export async function getAllAdminLiasses(): Promise<AdminLiasse[]> {
    const res = await apiRequest<ApiResponse<AdminLiasse[]>>('/liasses');
    return res.data;
}
